import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  twitterCardType?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  pageType?: 'home' | 'about' | 'contact' | 'legal' | 'service' | 'blog' | 'article' | 'sitemap';
  structuredData?: Record<string, any>;
  location?: string;
  image?: string;
  article?: {
    publishedTime: string;
    author: string;
    tags: string[];
  };
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  twitterCardType = 'summary_large_image',
  noIndex = false,
  pageType = 'website',
  structuredData,
  location,
  image,
  article,
}) => {
  const defaultTitle = 'UPTREND Systems | Custom Software Built for Growth';
  const defaultDescription =
    'UPTREND Systems provides custom software development in Whitby and workflow automation solutions across Ontario. We unify your workflows in a single, tailored platform built to manage your unique business needs.';
  const defaultKeywords =
    'custom software development, business software solutions, workflow automation, app development Whitby, Ontario software company, Canada tech firms, business process optimization, Durham Region tech, Whitby software development';
  const defaultOgImage = '/logo.png';
  const siteUrl = 'https://uptrendsystems.com';

  const seoTitle = title ? `${title} | UPTREND Systems` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoOgImage = ogImage || defaultOgImage;
  const seoCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;

  // Base organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'UPTREND Systems',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: defaultDescription,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Whitby',
      addressRegion: 'ON',
      addressCountry: 'CA'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '',
      contactType: 'customer service',
      email: 'info@uptrendsystems.com'
    },
    sameAs: [
      'https://instagram.com/uptrendsystems',
      'https://www.linkedin.com/company/uptrend-systems/',
    ],
  };

  // Generate appropriate structured data based on page type
  const getStructuredData = () => {
    // If custom structured data is provided, use that
    if (structuredData) {
      return structuredData;
    }

    // Otherwise generate based on page type
    switch (pageType) {
      case 'home':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'UPTREND Systems',
          url: siteUrl,
          description: seoDescription,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${siteUrl}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          },
          publisher: organizationSchema
        };
      
      case 'about':
        return {
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: seoTitle,
          description: seoDescription,
          url: seoCanonicalUrl,
          mainEntity: organizationSchema,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'About',
                item: `${siteUrl}/about`
              }
            ]
          }
        };
      
      case 'contact':
        return {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: seoTitle,
          description: seoDescription,
          url: seoCanonicalUrl,
          mainEntity: {
            '@type': 'Organization',
            name: 'UPTREND Systems',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'info@uptrendsystems.com',
              contactType: 'customer service'
            }
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Contact',
                item: `${siteUrl}/contact`
              }
            ]
          }
        };
      
      case 'service':
        return {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: title || 'Software Development',
          provider: organizationSchema,
          description: seoDescription,
          name: seoTitle,
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            areaServed: {
              '@type': 'GeoCircle',
              geoMidpoint: {
                '@type': 'GeoCoordinates',
                latitude: 43.8975,
                longitude: -78.9429
              },
              geoRadius: '100km'
            }
          }
        };
      
      case 'legal':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['h1', 'h2']
          },
          name: seoTitle,
          description: seoDescription,
          url: seoCanonicalUrl,
          publisher: organizationSchema,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: title || 'Legal',
                item: seoCanonicalUrl
              }
            ]
          }
        };
      
      case 'blog':
        return {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: seoTitle,
          description: seoDescription,
          url: seoCanonicalUrl,
          publisher: organizationSchema,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: `${siteUrl}/blog`
              }
            ]
          }
        };
      
      case 'article':
        return {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: title || 'Article',
          description: seoDescription,
          image: image || defaultOgImage,
          datePublished: article?.publishedTime || new Date().toISOString(),
          dateModified: articleModifiedTime || article?.publishedTime || new Date().toISOString(),
          author: {
            '@type': 'Person',
            name: article?.author || 'UPTREND Systems'
          },
          publisher: organizationSchema,
          url: seoCanonicalUrl,
          keywords: seoKeywords,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': seoCanonicalUrl
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: `${siteUrl}/blog`
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: title || 'Article',
                item: seoCanonicalUrl
              }
            ]
          }
        };
      
      case 'sitemap':
        return {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Sitemap',
          description: 'Navigation map for UPTREND Systems website',
          url: seoCanonicalUrl,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Sitemap',
                item: `${siteUrl}/sitemap`
              }
            ]
          }
        };
      
      default:
        return organizationSchema;
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {canonicalUrl && <link rel="canonical" href={seoCanonicalUrl} />}
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={seoCanonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={image || seoOgImage} />
      <meta property="og:image:alt" content="UPTREND Systems" />
      <meta property="og:site_name" content="UPTREND Systems" />
      <meta property="og:locale" content="en_CA" />
      
      {/* Article specific Open Graph tags */}
      {pageType === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:author" content={article.author} />
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Legacy article OG tags for backward compatibility */}
      {ogType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === 'article' && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCardType} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={image || seoOgImage} />
      
      {/* GEO Meta Tags */}
      <meta name="geo.region" content="CA-ON" />
      {location && <meta name="geo.placename" content={location} />}
      
      {/* Favicon and PWA Meta */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};

export default SEO; 